<template>
  <div class="side-menu">
    <div class="side-menu__logo" @click="router.push('/index')">
      <img :src="logoUrl" alt="logo" class="side-menu__logo-img" />
      <span v-show="!appStore.sidebarCollapsed" class="side-menu__logo-text">
        {{ domainName || '充电桩运营平台' }}
      </span>
    </div>

    <el-scrollbar class="side-menu__scroll">
      <el-menu
        :default-active="activeMenu"
        :collapse="appStore.sidebarCollapsed"
        background-color="#1f2d3d"
        text-color="#bfcbd9"
        active-text-color="#ffffff"
        unique-opened
        router
      >
        <el-menu-item index="/index" @click="go('/index')">
          <el-icon><HomeFilled /></el-icon>
          <template #title>首页</template>
        </el-menu-item>

        <!-- 模块分组：数据来自后端 /getMenu（地址自动转换为 Vue 路由） -->
        <el-sub-menu v-for="group in permissionStore.menuGroups" :key="group.title" :index="group.title">
          <template #title>
            <el-icon><Menu /></el-icon>
            <span>{{ group.title }}</span>
          </template>
          <el-menu-item
            v-for="item in group.children"
            :key="item.id"
            :index="item.path"
            @click="go(item.path)"
          >
            {{ item.title }}
          </el-menu-item>
        </el-sub-menu>
      </el-menu>
    </el-scrollbar>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from '@/store/app'
import { useUserStore } from '@/store/user'
import { usePermissionStore } from '@/store/permission'
import { API_BASE } from '@/settings'

/**
 * 侧边菜单
 *
 * 关键：菜单数据直接来自后端 /getMenu，permission store 会把 menuUrl
 * （形如 /html/sitemanage/pileList.html）自动转成 Vue 路由，
 * 因此【后端菜单表与权限配置无需任何改动】。
 */

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
const userStore = useUserStore()
const permissionStore = usePermissionStore()

const activeMenu = computed(() => route.path)
const domainName = computed(() => userStore.systemConfig?.domainName || '')
const logoUrl = computed(() => {
  const id = userStore.systemConfig?.logo
  return id ? `${API_BASE}/filedownload?fileId=${id}` : '/logo.svg'
})

function go(path: string) {
  if (route.fullPath !== path) router.push(path)
}

onMounted(async () => {
  if (!permissionStore.loaded) {
    await permissionStore.loadMenu()
  }
})

watch(
  () => userStore.isLogged,
  (v) => {
    if (v && !permissionStore.loaded) permissionStore.loadMenu()
  }
)
</script>

<style scoped lang="scss">
.side-menu {
  height: 100%;
  display: flex;
  flex-direction: column;

  &__logo {
    height: $header-height;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    cursor: pointer;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    &-img {
      width: 32px;
      height: 32px;
      object-fit: contain;
      flex-shrink: 0;
    }

    &-text {
      color: #fff;
      font-size: 14px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__scroll {
    flex: 1;
  }

  :deep(.el-menu) {
    border-right: none;
  }

  :deep(.el-menu-item.is-active) {
    background-color: $c-primary !important;
  }
}
</style>
